import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement, RouteTo } from '~/utils/rbac'
import { moduleIndexPermission, type ModuleScreen } from '~/utils/rbacModuleIndex'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Les cinq onglets vivent sur la même route (`/visio-reviews`), l'onglet courant est un état local. */
export type VisioReviewsTab = 'veille' | 'evaluation' | 'global' | 'boutique' | 'operateur'
export const INTERACTION_READ: PermissionTuple = [
  'read',
  'visio.yesterday-interactions.interaction',
]
export const EXCHANGE_READ: PermissionTuple = ['read', 'visio.exchanges-to-evaluate.exchange']
export const OVERVIEW_READ: PermissionTuple = ['read', 'visio.overview.overview']
export const ADMINISTRATION_READ: PermissionTuple = ['read', 'visio.administration.administration']
export const ADMINISTRATION_EDIT: PermissionTuple = ['edit', 'visio.administration.administration']

/**
 * Noter un échange, valider la note IA, changer son statut de résolution : les trois écritures de la
 * modale d'évaluation, ouverte depuis n'importe laquelle des trois listes du module.
 */
export const EVALUATION_EDIT: PermissionTuple = ['edit', 'visio.exchanges-to-evaluate.evaluation']

/**
 * Source unique des droits de lecture des sous-onglets. Consommée par la barre d'onglets et par le
 * `staticData.permission` de `/visio-reviews` : un seul endroit à mettre à jour, pas de dérive
 * possible entre l'onglet affiché et son contenu.
 *
 * « Vue d'ensemble », « Par Boutique » et « Par Opérateur » sont les trois angles d'une même
 * ressource, d'où le tuple partagé.
 */
export const VISIO_REVIEWS_TAB_PERMISSIONS = {
  veille: INTERACTION_READ,
  evaluation: EXCHANGE_READ,
  global: OVERVIEW_READ,
  boutique: OVERVIEW_READ,
  operateur: OVERVIEW_READ,
} as const satisfies Record<VisioReviewsTab, PermissionRequirement>

/** Les écrans du module, dans l'ordre de repli de la redirection de l'index. */
export const VISIO_REVIEWS_SCREENS: ModuleScreen<RouteTo>[] = [
  { to: '/visio-reviews/veille', permission: INTERACTION_READ },
  { to: '/visio-reviews/evaluation', permission: EXCHANGE_READ },
  { to: '/visio-reviews/global', permission: OVERVIEW_READ },
  { to: '/visio-reviews/boutique', permission: OVERVIEW_READ },
  { to: '/visio-reviews/operateur', permission: OVERVIEW_READ },
  { to: '/visio-reviews/admin', permission: ADMINISTRATION_READ },
]

const VISIO_REVIEWS_SCREEN_PERMISSIONS = VISIO_REVIEWS_SCREENS.map((screen) => screen.permission)

/**
 * Accès à l'écran : dès qu'un seul de ses écrans est accessible, administration comprise.
 *
 * L'administration du module n'est pas un onglet (elle s'ouvre par un bouton du layout) mais c'est
 * un écran du module : sans elle ici, un rôle porteur du seul `read visio.administration...` voyait
 * l'entrée de menu et se faisait refuser l'index (audit du 19/09, BLOQ-02).
 */
export const VISIO_REVIEWS_PAGE_READ: PermissionRequirement = moduleIndexPermission(
  VISIO_REVIEWS_SCREEN_PERMISSIONS,
)

/**
 * Configuration du module (seuils de notation, critères d'évaluation IA) : n'importe quel écran la
 * lit pour colorer les notes, seule l'administration la modifie. Même exigence côté backend sur
 * `GET /visio-reviews/config`.
 */
export const VISIO_REVIEWS_CONFIG_READ: PermissionRequirement = {
  any: [ADMINISTRATION_READ, INTERACTION_READ, EXCHANGE_READ, OVERVIEW_READ],
}

/**
 * Chaque onglet est une route à part entière : son exigence est reprise ici telle qu'elle est
 * déclarée dans le `staticData.permission` de la route (même constante), pour que la barre
 * d'onglets n'ait pas à interroger le registre de routes.
 */
type VisioReviewsTabDefinition = GuardedTab<VisioReviewsTab> & {
  /** Resserré sur `string` : `AppTabs` n'accepte pas un `ReactNode` quelconque en libellé. */
  label: string
  to: RouteTo
  permission: PermissionRequirement
}

/** Ordre de déclaration = ordre d'affichage, et ordre de repli quand l'onglet actif est interdit. */
export const VISIO_REVIEWS_TABS: VisioReviewsTabDefinition[] = [
  {
    value: 'veille',
    label: 'Interactions de la Veille',
    to: '/visio-reviews/veille',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.veille,
  },
  {
    value: 'evaluation',
    label: 'Échanges à Évaluer',
    to: '/visio-reviews/evaluation',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.evaluation,
  },
  {
    value: 'global',
    label: "Vue d'ensemble",
    to: '/visio-reviews/global',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.global,
  },
  {
    value: 'boutique',
    label: 'Par Boutique',
    to: '/visio-reviews/boutique',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.boutique,
  },
  {
    value: 'operateur',
    label: 'Par Opérateur',
    to: '/visio-reviews/operateur',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.operateur,
  },
]
